import { useMemo } from "react";
import {
  buildPathNetwork,
  buildNodeKindRefs,
  logPathNetworkStats,
  PATH_LAYER_TYPES,
  type PathNetwork,
} from "@wondermaps/maps";
import type { Feature, LineString, Polygon, MultiPolygon, Geometry } from "geojson";

/** Layer shape expected from map data */
interface LayerWithGeom {
  id: string;
  layerType: string;
  geom: unknown;
  styleJson?: unknown;
}

/** Reference sets for diagnostic node-kind classification (junction / polygon-vertex / midline). */
export interface NodeKindRefs {
  lineEndpointKeys: Set<string>;
  polygonVertexKeys: Set<string>;
}

/**
 * Hook to build path network from layers data
 * @param layers - Array of layers from map data (pass mapData.version.layers)
 * @returns { network, nodeKindRefs } - The built network + the keys needed to
 *   classify nodes for diagnostic logging. `nodeKindRefs` is null when network is null.
 */
export function usePathNetwork(layers: LayerWithGeom[] | undefined): {
  network: PathNetwork | null;
  nodeKindRefs: NodeKindRefs | null;
} {
  const result = useMemo(() => {
    if (!layers) return { network: null, nodeKindRefs: null };

    const pathFeatures: Feature<LineString | Polygon | MultiPolygon>[] = [];

    for (const layer of layers) {
      if (!PATH_LAYER_TYPES.includes(layer.layerType)) continue;
      if (!layer.geom) continue;

      const geom = layer.geom as unknown as Geometry;
      const style = layer.styleJson as { hasStairs?: boolean; steepGrade?: boolean; wayfindingEnabled?: boolean; wayfindingWeight?: number } | null;
      if (style?.wayfindingEnabled === false) continue;
      const props = {
        layerId: layer.id,
        layerType: layer.layerType,
        hasStairs: style?.hasStairs ?? false,
        steepGrade: style?.steepGrade ?? false,
        wayfindingWeight: style?.wayfindingWeight ?? 1.0,
      };

      if (geom.type === "LineString") {
        pathFeatures.push({ type: "Feature", geometry: geom, properties: props });
      } else if (geom.type === "MultiLineString") {
        // Split MultiLineString into individual LineStrings.
        for (const coords of geom.coordinates) {
          pathFeatures.push({
            type: "Feature",
            geometry: { type: "LineString", coordinates: coords },
            properties: props,
          });
        }
      } else if (geom.type === "Polygon" || geom.type === "MultiPolygon") {
        // Plaza polygons (path_wide). Pass through unsplit — buildPathNetwork
        // owns perimeter + visibility-graph synthesis.
        pathFeatures.push({ type: "Feature", geometry: geom, properties: props });
      }
    }

    if (pathFeatures.length === 0) return { network: null, nodeKindRefs: null };

    const network = buildPathNetwork(pathFeatures);
    const nodeKindRefs = buildNodeKindRefs(pathFeatures);
    const polygonCount = pathFeatures.filter(
      (f) => f.geometry.type === "Polygon" || f.geometry.type === "MultiPolygon",
    ).length;
    const lineCount = pathFeatures.length - polygonCount;
    logPathNetworkStats(network, "visitor", { polygonCount, lineCount });
    return { network, nodeKindRefs };
  }, [layers]);

  return result;
}
